import { useState } from 'react';
import { Controller, useForm, type SubmitHandler } from 'react-hook-form';
import { zodResolver } from '@hookform/resolvers/zod';
import { useMutation, useQueryClient } from '@tanstack/react-query';
import { unwrap } from '@truecare/api-client';
import {
  IANA_TIMEZONE_LABELS,
  RecipientCreateRequestSchema,
  RecipientPatchRequestSchema,
  type IANATimezone,
  type RecipientCreateRequest,
  type RecipientPatchRequest,
  type RecipientResponse,
} from '@truecare/schemas';
import { Alert, Button, DateInput, Field, Input } from '@truecare/ui';
import { api } from '../api';

interface Props {
  mode: 'create' | 'edit';
  /** Required in edit mode — the recipient row id for the PATCH path
   *  param. Ignored in create mode. */
  recipientId?: string;
  /** Pre-filled values in edit mode; defaults supplied in create. */
  initial?: Partial<RecipientCreateRequest>;
  /** Called after a successful submit so the parent can collapse the
   *  form / refresh state. */
  onSuccess: () => void;
  onCancel?: () => void;
}

const RELATIONSHIP_OPTIONS = [
  ['SELF', 'Myself'],
  ['SPOUSE', 'Spouse'],
  ['ADULT_CHILD', 'Parent (I\'m the adult child)'],
  ['PARENT', 'Child (I\'m the parent)'],
  ['SIBLING', 'Sibling'],
  ['POA', 'Power of attorney'],
  ['GUARDIAN', 'Legal guardian'],
  ['OTHER', 'Other'],
] as const;

/**
 * Shared add / edit form for a Recipient under the signed-in
 * HomecareClient account. Validates against the same Zod schema the
 * backend uses (`RecipientCreate/PatchRequestSchema`) so field rules
 * can't drift.
 *
 * On success: invalidates the `['homecare', 'me']` query so the
 * recipient list refreshes, then calls onSuccess to let the parent
 * collapse the form.
 */
export function RecipientForm({
  mode,
  recipientId,
  initial,
  onSuccess,
  onCancel,
}: Props) {
  const queryClient = useQueryClient();
  const [submitError, setSubmitError] = useState<string | null>(null);

  const schema = mode === 'create' ? RecipientCreateRequestSchema : RecipientPatchRequestSchema;
  const {
    register,
    control,
    handleSubmit,
    formState: { errors, isSubmitting },
  } = useForm<RecipientCreateRequest | RecipientPatchRequest>({
    resolver: zodResolver(schema),
    defaultValues: {
      name: initial?.name ?? '',
      dateOfBirth: initial?.dateOfBirth ?? null,
      relationshipToAccountHolder: initial?.relationshipToAccountHolder ?? 'SELF',
      serviceAddressLine1: initial?.serviceAddressLine1 ?? '',
      serviceAddressLine2: initial?.serviceAddressLine2 ?? null,
      serviceAddressCity: initial?.serviceAddressCity ?? '',
      serviceAddressProvince: initial?.serviceAddressProvince ?? 'ON',
      serviceAddressPostalCode: initial?.serviceAddressPostalCode ?? '',
      timezone: initial?.timezone ?? 'America/Toronto',
    },
  });

  const createMutation = useMutation({
    mutationFn: (values: RecipientCreateRequest) =>
      unwrap(
        api.POST('/api/v1/me/homecare/recipients', { body: values }),
      ),
    onSuccess: () => {
      void queryClient.invalidateQueries({ queryKey: ['homecare', 'me'] });
      onSuccess();
    },
  });

  const patchMutation = useMutation({
    mutationFn: (values: RecipientPatchRequest) => {
      if (!recipientId) throw new Error('recipientId required for edit mode');
      return unwrap(
        api.PATCH('/api/v1/me/homecare/recipients/{id}', {
          params: { path: { id: recipientId } },
          body: values,
        }),
      );
    },
    onSuccess: () => {
      void queryClient.invalidateQueries({ queryKey: ['homecare', 'me'] });
      onSuccess();
    },
  });

  const onSubmit: SubmitHandler<RecipientCreateRequest | RecipientPatchRequest> = async (
    values,
  ) => {
    setSubmitError(null);
    try {
      if (mode === 'create') {
        await createMutation.mutateAsync(values as RecipientCreateRequest);
      } else {
        await patchMutation.mutateAsync(values as RecipientPatchRequest);
      }
    } catch (err) {
      setSubmitError(
        err instanceof Error
          ? err.message
          : 'Something went wrong — please try again.',
      );
    }
  };

  return (
    <form
      onSubmit={(event) => {
        void handleSubmit(onSubmit)(event);
      }}
      className="flex flex-col gap-4"
      noValidate
    >
      <div className="grid grid-cols-1 gap-4 md:grid-cols-2">
        <Field label="Name" error={errors.name?.message}>
          <Input
            type="text"
            autoComplete="name"
            disabled={isSubmitting}
            {...register('name')}
          />
        </Field>
        <Field
          label="Date of birth (optional)"
          error={errors.dateOfBirth?.message}
        >
          <Controller
            name="dateOfBirth"
            control={control}
            render={({ field }) => (
              <DateInput
                name={field.name}
                value={(field.value as string | undefined) ?? ''}
                onChange={field.onChange}
                disabled={isSubmitting}
              />
            )}
          />
        </Field>
        <Field
          label="Relationship to you"
          error={errors.relationshipToAccountHolder?.message}
        >
          <select
            className="mt-1 block w-full rounded-md border border-border bg-cream px-3 py-2 font-body text-sm text-ink shadow-sm transition focus:border-forest focus:outline-none focus:ring-1 focus:ring-forest disabled:opacity-60"
            disabled={isSubmitting}
            {...register('relationshipToAccountHolder')}
          >
            {RELATIONSHIP_OPTIONS.map(([value, label]) => (
              <option key={value} value={value}>
                {label}
              </option>
            ))}
          </select>
        </Field>
        <div /> {/* grid spacer */}

        <Field label="Address line 1" error={errors.serviceAddressLine1?.message}>
          <Input
            type="text"
            autoComplete="street-address"
            disabled={isSubmitting}
            {...register('serviceAddressLine1')}
          />
        </Field>
        <Field
          label="Address line 2 (optional)"
          error={errors.serviceAddressLine2?.message}
        >
          <Input
            type="text"
            disabled={isSubmitting}
            {...register('serviceAddressLine2')}
          />
        </Field>
        <Field label="City" error={errors.serviceAddressCity?.message}>
          <Input
            type="text"
            autoComplete="address-level2"
            disabled={isSubmitting}
            {...register('serviceAddressCity')}
          />
        </Field>
        <Field
          label="Postal code"
          error={errors.serviceAddressPostalCode?.message}
        >
          <Input
            type="text"
            autoComplete="postal-code"
            disabled={isSubmitting}
            {...register('serviceAddressPostalCode')}
          />
        </Field>
        <Field label="Time zone" error={errors.timezone?.message}>
          <select
            className="mt-1 block w-full rounded-md border border-border bg-cream px-3 py-2 font-body text-sm text-ink shadow-sm transition focus:border-forest focus:outline-none focus:ring-1 focus:ring-forest disabled:opacity-60"
            disabled={isSubmitting}
            {...register('timezone')}
          >
            {(Object.entries(IANA_TIMEZONE_LABELS) as [IANATimezone, string][]).map(
              ([value, label]) => (
                <option key={value} value={value}>
                  {label}
                </option>
              ),
            )}
          </select>
        </Field>
      </div>

      {submitError && <Alert tone="danger" title="Couldn't save recipient">{submitError}</Alert>}

      <div className="flex gap-3">
        <Button type="submit" disabled={isSubmitting}>
          {isSubmitting
            ? 'Saving…'
            : mode === 'create'
              ? 'Add recipient'
              : 'Save changes'}
        </Button>
        {onCancel && (
          <Button type="button" variant="secondary" onClick={onCancel} disabled={isSubmitting}>
            Cancel
          </Button>
        )}
      </div>
    </form>
  );
}

export type { RecipientResponse };
